import { isScoredInteractionMode } from "@/lib/scenarios/interactionModes";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLiveAvatarStream } from "@/hooks/use-liveavatar-stream";
import { useBubbleSize } from "@/hooks/useBubbleSize";
import type { Scenario, ScoreResult } from "@/lib/powtoon-be/types";
import { liveavatarSessionAdapter } from "@/lib/scenarios/providers/liveavatar/sessionAdapter";
import { useSessionTracking } from "@/hooks/useSessionTracking";
import { AvatarVideoArea } from "./AvatarVideoArea";
import { PreSessionScreen } from "./PreSessionScreen";
import { AiConsentGate } from "./AiDisclaimerBanner";
import { shouldShowAiNotice } from "@/lib/scenarios/aiNotice";
import { SessionStartOverlay } from "./SessionStartOverlay";
import { PostSessionScreen } from "./PostSessionScreen";
import { SalesCtaPanel } from "./SalesCtaPanel";
import { SessionControls } from "./SessionControls";
import { WaitingForScoreScreen } from "./WaitingForScoreScreen";
import { ScoreScreen } from "./ScoreScreen";
import { DisplayDocument } from "./DisplayDocument";
import { DraggableAvatarBubble } from "./DraggableAvatarBubble";
import { postToAncestors } from "./postToAncestors";
import { withCacheBust } from "@/lib/utils";
import { trackEvent } from "@/services/analytics";
import { ANALYTICS_CATEGORIES, ANALYTICS_ACTIONS } from "@/config/analytics-config";
import { usePowtoonAuth } from "@/lib/auth/PowtoonAuthContext";
import { shouldUseByoLivekit } from "@/lib/auth/orgSettings";
import { TavusFrame } from "./frames/TavusFrame";
import { DidFrame } from "./frames/DidFrame";
import { KalturaAgentFrame } from "./frames/KalturaAgentFrame";

type PageState =
  | "idle"
  | "preparing"
  | "ready"
  | "going-live"
  | "connected"
  | "awaiting-score"
  | "scored"
  | "ended";

const postToParent = postToAncestors;

export function SessionFrame(props: { scenario: Scenario; publicLinkToken?: string }) {
  if (!shouldShowAiNotice(props.scenario.content)) return <SessionFrameInner {...props} />;
  return (
    <AiConsentGate>
      <SessionFrameInner {...props} />
    </AiConsentGate>
  );
}

function SessionFrameInner({
  scenario,
  publicLinkToken,
}: {
  scenario: Scenario;
  publicLinkToken?: string;
}) {
  // Tavus scenarios run through a completely separate frame — different
  // provider, different transport (Daily), different content shape (no
  // `context_block`, uses `pal`). Branching here keeps the rest of this
  // file focused on the LiveAvatar flow. The provider is stable for the
  // lifetime of a given scenario mount, so this branch is hook-safe.

  if (scenario.content?.provider === "tavus") {
    return <TavusFrame scenario={scenario} publicLinkToken={publicLinkToken} />;
  }

  if (scenario.content?.provider === "did") {
    return <DidFrame scenario={scenario} publicLinkToken={publicLinkToken} />;
  }

  if (scenario.content?.provider === "kaltura") {
    // Kaltura runs on its Agent product: it owns the conversation loop and we
    // render it headlessly in our own chrome, capturing its transcript events.
    return <KalturaAgentFrame scenario={scenario} publicLinkToken={publicLinkToken} />;
  }
  const content = scenario.content ?? {};
  const { avatar_id, voice_id, language, auto_start, context_id, llm_configuration_id } = content;
  const openingText = content.context_block?.opening_text;
  const systemPrompt = content.context_block?.generated_prompt;
  const ctaType = content.cta_type ?? "none";
  const ctaUrl = content.cta_url?.trim();
  const redirectUrl = content.redirect_url?.trim();
  const showSalesCta = ctaType === "sales" && !!ctaUrl;
  const ready = Boolean(avatar_id && context_id);
  const { orgDomain } = usePowtoonAuth();
  const interactionMode = content.context_block?.interaction_mode;
  const isScoredMode = isScoredInteractionMode(interactionMode);
  const display = content.display;
  const hasDisplay = !!display?.source && !showSalesCta;
  const displayLayout = display?.layout ?? "side-by-side";
  const avatarPosition: "left" | "right" = display?.avatar_position ?? "right";

  const [pageState, setPageState] = useState<PageState>("idle");
  const [isMuted, setIsMuted] = useState(true);
  const [isPaused, setIsPaused] = useState(false);
  const [isEnding, setIsEnding] = useState(false);
  const [streamError, setStreamError] = useState<string | null>(null);
  const [score, setScore] = useState<ScoreResult | null>(null);
  const [scoreError, setScoreError] = useState<string | null>(null);
  const initialBubbleCorner: "tl" | "tr" = avatarPosition === "left" ? "tl" : "tr";
  const {
    containerRef: bubbleContainerRef,
    size: bubbleSize,
    onResizeStart,
    onResizeMove,
    onResizeEnd,
  } = useBubbleSize();
  const trackedRef = useRef(false);
  const autoStartedRef = useRef(false);
  const autoInFlightRef = useRef(false);
  const pageStateRef = useRef<PageState>("idle");
  useEffect(() => {
    pageStateRef.current = pageState;
  }, [pageState]);

  const tracking = useSessionTracking({
    scenario,
    publicLinkToken,
    adapter: liveavatarSessionAdapter,
  });

  // Current tenant policy overrides old mode stamps. Anonymous links have no
  // owner-tenant metadata, so preserve their saved route rather than changing
  // other companies' BYO sessions based on an unknown tenant.
  const useByoLivekit = shouldUseByoLivekit(orgDomain, content.mode, content.force_byo_livekit);
  useEffect(() => {
    console.info("[liveavatar] session mode", {
      mode: content.mode ?? "full",
      useByoLivekit,
      orgDomain,
      href: typeof window !== "undefined" ? window.location.href : null,
      inIframe: typeof window !== "undefined" ? window.parent !== window : false,
    });
  }, [useByoLivekit, content.mode, orgDomain]);

  const {
    state: streamState,
    mediaStream,
    sessionId,
    prepareSession,
    goLive,
    stopSession,
    pauseSession,
    resumeSession,
    setMicEnabled,
  } = useLiveAvatarStream({ onError: (e) => setStreamError(e), useByoLivekit });

  useEffect(() => {
    if (streamState !== "connected") return;
    const id = sessionId.current;
    if (!id) return;
    tracking.markStarted();
    postToParent({ event: "sessionStarted", sessionId: id, scenarioId: scenario.id });
    trackEvent({
      category: ANALYTICS_CATEGORIES.SESSION,
      action: ANALYTICS_ACTIONS.STARTED,
      ga4EventName: "session_started",
      extraData: { scenario_id: scenario.id, session_id: id, mode: interactionMode },
    });
  }, [streamState, scenario.id, sessionId, tracking]);

  useEffect(() => {
    if (streamState === "ready" && pageState === "preparing") setPageState("ready");
    if (streamState === "connected" && pageState === "going-live") setPageState("connected");
    if (streamState === "idle" && pageState === "connected") setPageState("ended");
  }, [streamState, pageState]);

  const handleOpen = useCallback(
    async (lang: string) => {
      if (!ready || !context_id) return;
      setPageState("preparing");
      setStreamError(null);
      try {
        await prepareSession({
          avatar_id,
          context_id,
          voice_id,
          language: lang,
          llm_configuration_id,
          system_prompt: systemPrompt,
          opening_text: openingText,
        });
      } catch (err) {
        setStreamError((err as Error).message || "Failed to prepare session.");
        setPageState("idle");
      }
    },
    [
      ready,
      avatar_id,
      context_id,
      voice_id,
      llm_configuration_id,
      systemPrompt,
      openingText,
      prepareSession,
    ],
  );

  const handlePlay = useCallback(async () => {
    setPageState("going-live");
    setStreamError(null);
    setIsPaused(false);
    try {
      await goLive(openingText || undefined);
    } catch (err) {
      setStreamError((err as Error).message || "Failed to start session.");
      setPageState("ready");
    }
  }, [goLive, openingText]);

  const runAutoStart = useCallback(async () => {
    if (autoInFlightRef.current || !ready || !context_id) return;
    autoInFlightRef.current = true;
    autoStartedRef.current = true;
    setStreamError(null);
    setIsPaused(false);
    try {
      setPageState("preparing");
      await prepareSession({
        avatar_id,
        context_id,
        voice_id,
        language: language || "en",
        llm_configuration_id,
        system_prompt: systemPrompt,
        opening_text: openingText,
      });
    } catch (err) {
      setStreamError((err as Error).message || "Failed to start session.");
      setPageState("ended");
    } finally {
      autoInFlightRef.current = false;
    }
  }, [
    ready,
    avatar_id,
    context_id,
    voice_id,
    language,
    llm_configuration_id,
    systemPrompt,
    openingText,
    prepareSession,
  ]);

  useEffect(() => {
    if (!ready || !auto_start || autoStartedRef.current) return;
    runAutoStart();
  }, [ready, auto_start, runAutoStart]);

  const handleEnd = useCallback(async () => {
    setIsEnding(true);
    tracking.markEnded();
    trackEvent({
      category: ANALYTICS_CATEGORIES.SESSION,
      action: ANALYTICS_ACTIONS.COMPLETED,
      ga4EventName: "session_completed",
      extraData: {
        scenario_id: scenario.id,
        session_id: sessionId.current,
        mode: interactionMode,
      },
    });
    // Show the processing screen immediately when the user closes an MCQ session.
    // Tracking / transcript / scoring continue in the background.
    if (isScoredMode) {
      setScore(null);
      setScoreError(null);
      setPageState("awaiting-score");
    }
    try {
      await stopSession();
    } catch {
      /* ignore */
    }
    setIsPaused(false);
    setIsMuted(true);
    const sid = sessionId.current;
    let trackResult: { transcriptObjectId: string | null } = {
      transcriptObjectId: null,
    };
    if (sid && !trackedRef.current) {
      trackedRef.current = true;
      trackResult = await tracking.trackOnEnd({ sessionId: sid });
    }
    if (redirectUrl) {
      postToParent({ event: "sessionEnded", sessionId: sid, scenarioId: scenario.id });
      window.location.href = redirectUrl;
      return;
    }
    const transcriptObjectId = trackResult.transcriptObjectId;
    if (isScoredMode && transcriptObjectId) {
      const { result, error, aborted } = await tracking.runScoringJob(transcriptObjectId);
      if (aborted) return;
      if (result) {
        setScore(result);
        postToParent({
          event: "sessionScored",
          sessionId: sid,
          scenarioId: scenario.id,
          score: result,
        });
      } else if (error) {
        setScoreError(error);
      }
      setPageState("scored");
      postToParent({ event: "sessionEnded", sessionId: sid, scenarioId: scenario.id });
      return;
    }
    postToParent({ event: "sessionEnded", sessionId: sid, scenarioId: scenario.id });
    setPageState("ended");
  }, [stopSession, redirectUrl, scenario.id, sessionId, isScoredMode, tracking]);

  const handleRestart = useCallback(async () => {
    setStreamError(null);
    setIsEnding(false);
    autoStartedRef.current = false;
    autoInFlightRef.current = false;
    trackedRef.current = false;
    setIsMuted(true);
    setScore(null);
    setScoreError(null);
    tracking.reset();
    try {
      await stopSession();
    } catch {
      /* ignore */
    }
    if (auto_start) runAutoStart();
    else setPageState("idle");
  }, [stopSession, auto_start, runAutoStart, tracking]);

  // If the host removes the iframe (or the user closes the tab) while a
  // session is in-flight, fire a best-effort beacon so the session and any
  // transcript we have are still tracked server-side. Uses `fetch` with
  // `keepalive`, which survives unload. Only the public-link path is
  // covered — authenticated trainer sessions still rely on the normal
  // `handleEnd` flow.
  useEffect(() => {
    const handler = () => {
      const st = pageStateRef.current;
      const live =
        st === "preparing" || st === "ready" || st === "going-live" || st === "connected";
      if (!live || trackedRef.current) return;
      trackedRef.current = true;
      const sid = sessionId.current;
      try {
        stopSession();
      } catch {
        /* ignore */
      }
      tracking.beaconOnUnload(sid);
    };
    window.addEventListener("pagehide", handler);
    window.addEventListener("beforeunload", handler);
    return () => {
      window.removeEventListener("pagehide", handler);
      window.removeEventListener("beforeunload", handler);
    };
  }, [sessionId, stopSession, tracking]);

  const handleBack = useCallback(() => {
    postToParent({ event: "sessionBack", scenarioId: scenario.id });
    if (typeof window !== "undefined" && window.parent === window) {
      try {
        window.history.back();
      } catch {
        /* ignore */
      }
    }
  }, [scenario.id]);

  const handleBackToConfigure = useCallback(async () => {
    try {
      await stopSession();
    } catch {
      /* ignore */
    }
    setIsPaused(false);
    setIsMuted(true);
    setStreamError(null);
    autoStartedRef.current = false;
    autoInFlightRef.current = false;
    setPageState("idle");
  }, [stopSession]);

  if (!ready) {
    return (
      <div className="h-screen w-screen grid place-items-center bg-background text-center px-6">
        <p className="text-sm text-muted-foreground">
          This scenario is misconfigured. Open the editor to fix it.
        </p>
      </div>
    );
  }

  if (pageState === "awaiting-score") {
    return <WaitingForScoreScreen scenario={scenario} />;
  }

  if (pageState === "scored") {
    return (
      <ScoreScreen scenario={scenario} score={score} error={scoreError} onRestart={handleRestart} />
    );
  }

  // Teardown / transcript / scoring window: show the post-session screen with
  // restart disabled instead of falling through to the start screen.
  if (isEnding && pageState !== "ended") {
    return <PostSessionScreen scenario={scenario} onRestart={handleRestart} busy />;
  }

  const hasVideo =
    !!mediaStream && mediaStream.getVideoTracks().some((t) => t.readyState === "live");

  if (!auto_start && (pageState === "idle" || pageState === "preparing")) {
    const screen = (
      <PreSessionScreen
        scenario={scenario}
        connecting={pageState === "preparing"}
        onStart={handleOpen}
        onBack={handleBack}
      />
    );
    if (showSalesCta) {
      return (
        <div className="flex flex-col lg:flex-row h-[100dvh] bg-background overflow-hidden">
          <div className="flex-1 min-h-0 lg:w-[65%]">{screen}</div>
          <div className="lg:w-[35%] border-t lg:border-t-0 lg:border-l border-border min-h-[40vh] lg:min-h-0">
            <SalesCtaPanel bookingUrl={ctaUrl!} />
          </div>
        </div>
      );
    }
    return screen;
  }

  if (
    auto_start &&
    !hasVideo &&
    (pageState === "preparing" || pageState === "going-live" || pageState === "idle")
  ) {
    // Auto-start reuses the shared pre-session UI as the connecting state
    // (thumbnail + spinner-in-button) so both providers show a single,
    // consistent loading experience.
    return (
      <PreSessionScreen
        scenario={scenario}
        connecting
        onStart={() => {
          /* auto-start already fires from the effect above */
        }}
      />
    );
  }

  if (pageState === "ended") {
    return (
      <PostSessionScreen
        scenario={scenario}
        subtitle={streamError ?? undefined}
        onRestart={handleRestart}
      />
    );
  }

  const isLive = hasVideo;
  const isGoingLive = pageState === "going-live";

  return (
    <div
      className={
        "flex bg-background overflow-hidden " +
        (showSalesCta ? "flex-col lg:flex-row h-[100dvh]" : "flex-col h-[100dvh]")
      }
    >
      <div
        className={
          "relative flex flex-col min-h-0 " + (showSalesCta ? "flex-1 lg:w-[65%]" : "flex-1")
        }
      >
        {isLive ? (
          hasDisplay && displayLayout === "side-by-side" ? (
            <div
              className={
                "flex-1 min-h-0 flex flex-col lg:flex-row " +
                (avatarPosition === "left" ? "lg:flex-row-reverse" : "")
              }
            >
              <div
                className={
                  "flex-[2] min-h-0 lg:flex-none lg:w-2/3 lg:h-full " +
                  (avatarPosition === "left"
                    ? "border-b lg:border-b-0 lg:border-l border-border"
                    : "border-b lg:border-b-0 lg:border-r border-border")
                }
              >
                <DisplayDocument
                  scenarioId={scenario.id}
                  source={display!.source!}
                  publicLinkToken={publicLinkToken}
                />
              </div>
              <div className="flex-1 min-h-0 relative lg:flex-none lg:w-1/3 lg:h-full">
                <AvatarVideoArea
                  previewUrl={withCacheBust(scenario.thumb_url, scenario.modified)}
                  mediaStream={mediaStream}
                  isMuted={isMuted}
                  onToggleMute={async () => {
                    const next = !isMuted;
                    try {
                      await setMicEnabled(!next);
                      setIsMuted(next);
                    } catch {
                      /* surfaced via onError */
                    }
                  }}
                  isPaused={isPaused}
                  onTogglePause={() => {
                    if (isPaused) resumeSession();
                    else pauseSession();
                    setIsPaused(!isPaused);
                  }}
                  onEnd={handleEnd}
                  errorMessage={streamError}
                />
              </div>
            </div>
          ) : hasDisplay && displayLayout === "bubble" ? (
            <div ref={bubbleContainerRef} className="flex-1 min-h-0 relative">
              <DisplayDocument
                scenarioId={scenario.id}
                source={display!.source!}
                publicLinkToken={publicLinkToken}
              />
              <DraggableAvatarBubble
                initialCorner={initialBubbleCorner}
                size={bubbleSize}
                onResizeStart={onResizeStart}
                onResizeMove={onResizeMove}
                onResizeEnd={onResizeEnd}
              >
                <AvatarVideoArea
                  previewUrl={withCacheBust(scenario.thumb_url, scenario.modified)}
                  mediaStream={mediaStream}
                  isMuted={isMuted}
                  onToggleMute={async () => {
                    const next = !isMuted;
                    try {
                      await setMicEnabled(!next);
                      setIsMuted(next);
                    } catch {
                      /* surfaced via onError */
                    }
                  }}
                  isPaused={isPaused}
                  onTogglePause={() => {
                    if (isPaused) resumeSession();
                    else pauseSession();
                    setIsPaused(!isPaused);
                  }}
                  onEnd={handleEnd}
                  errorMessage={streamError}
                  fit="cover"
                  hideControls
                />
              </DraggableAvatarBubble>
              <SessionControls
                isMuted={isMuted}
                onToggleMute={async () => {
                  const next = !isMuted;
                  try {
                    await setMicEnabled(!next);
                    setIsMuted(next);
                  } catch {
                    /* surfaced via stream error */
                  }
                }}
                isPaused={isPaused}
                onTogglePause={() => {
                  if (isPaused) resumeSession();
                  else pauseSession();
                  setIsPaused(!isPaused);
                }}
                onEnd={handleEnd}
                className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20"
              />
            </div>
          ) : (
            <div className="flex-1 min-h-0 relative">
              <AvatarVideoArea
                previewUrl={withCacheBust(scenario.thumb_url, scenario.modified)}
                mediaStream={mediaStream}
                isMuted={isMuted}
                onToggleMute={async () => {
                  const next = !isMuted;
                  try {
                    await setMicEnabled(!next);
                    setIsMuted(next);
                  } catch {
                    /* surfaced via onError */
                  }
                }}
                isPaused={isPaused}
                onTogglePause={() => {
                  if (isPaused) resumeSession();
                  else pauseSession();
                  setIsPaused(!isPaused);
                }}
                onEnd={handleEnd}
                errorMessage={streamError}
              />
            </div>
          )
        ) : (
          <div className="flex-1 min-h-0 relative">
            <SessionStartOverlay
              previewUrl={withCacheBust(scenario.thumb_url, scenario.modified)}
              busy={isGoingLive}
              onStart={handlePlay}
              onBack={
                !auto_start && (pageState === "ready" || isGoingLive)
                  ? handleBackToConfigure
                  : undefined
              }
              errorMessage={streamError}
            />
          </div>
        )}
      </div>

      {showSalesCta && (
        <div className="lg:w-[35%] border-t lg:border-t-0 lg:border-l border-border min-h-[35vh] lg:min-h-0">
          <SalesCtaPanel bookingUrl={ctaUrl!} />
        </div>
      )}
    </div>
  );
}
